import { authenticatedFetch } from "@beholos/holoscommon";
import { API_BASE_URL } from './constants';
import { trackEvent } from './analytics.service';

export interface PaymentMethod {
  id: string;
  issuer?: {
    name: string;
  };
  last_four_digits?: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  card?: {
    brand?: string;
    last4?: string;
    exp_month?: number;
    exp_year?: number;
  };
}

export const cancel = async (
  reason: { comment: string; type: string },
  likelyHoodOfReturning: number | string
) => {
  try {

    const response = await authenticatedFetch(`${API_BASE_URL}/payments`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        reason: reason.comment || reason.type,
        likelyHoodOfReturning,
      }),
    });

    return await response.json();
  } catch (error) {
    console.error('Error canceling plan:', error);
    return null;
  }
};

export const acceptDiscount = async () => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/payments/cancelApplyDiscount`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await response.json();
  } catch (error) {
    console.error('Error accepting discount:', error);
    return null;
  }
};

export const getActivePlans = async () => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/payments/plans`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await response.json();
  } catch (error) {
    console.error('Error fetching active plans:', error);
    return null;
  }
}

export const getPaymentMethods = async () => {
  try {
    const response = await authenticatedFetch('/api/payments/methods', {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch payment methods');
    }

    const data: PaymentMethod[] = await response.json();

    return data
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const setDefaultPaymentMethod = async (cardId: string) => {
  try {
    const response = await authenticatedFetch(`/api/payments/methods/${cardId}`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to set default payment method');
    }

    return await response.json();
  } catch (error) {
    console.error('Error setting default payment method:', error);
    return null;
  }
};

export const deletePaymentMethod = async (cardId: string) => {
  try {
    const response = await authenticatedFetch(`/api/payments/methods/${cardId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to delete payment method');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting payment method:', error);
    return null;
  }
};

export const updatePaymentMethod = async ({ paymentMethodId }: { paymentMethodId: string }) => {
  try {
    const response = await authenticatedFetch('/api/payments/methods', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        paymentMethodId,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to update payment method');
    }

    return true;
  } catch (error) {
    console.error('Error updating payment method:', error);
    return null;
  }
};

export const updatePlan = async (
  {
    planId,
    paymentMethodId,
    endOfCycle,
    cardId,
    appliedPromoCode,
    smartFitAddon,
  }: {
    planId: string;
    paymentMethodId?: string;
    cardId?: string;
    endOfCycle: boolean;
    appliedPromoCode?: string;
    smartFitAddon?: {
      duration: 1 | 3;
    };
  }
) => {
  const response = await authenticatedFetch(`${API_BASE_URL}/payments/updatePlanMercadoPago`, {
    method: 'POST',
    body: JSON.stringify({
      planId,
      paymentMethodId,
      cardId,
      endOfCycle,
      appliedPromoCode,
      smartFitAddon,
    }),
  });

  if (!response.ok) {
    // Backend sends a user-facing message (e.g. MercadoPago rejection reason) in `message`
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to update plan');
  }

  trackEvent('plan_upgrade', { plan_id: planId });
  return await response.json();
}